import { toggleFieldAttributeV2 as toggleFieldAttribute } from "commonAssets/lib/toggleFieldAttributeV2";
import { toggleForeignPhone } from "commonAssets/lib/toggleForeignPhone.js";
import { decodeItemId, itemIdOf, toCurrentItemId } from "./utils";

const PREFIX_RE = /^([BCD])\.(\d+):\s*(.+)$/;
const SECTION_LETTERS = { B: 1, C: 2, D: 3 };

function normalizeLabel(label) {
    return label.replace(/\s*\([^)]*\)/g, "").trim();
}

/**
 * Parse a name like "B.2: Trade Date of Purchase/Acquisition".
 * Returns { itemIdOffset, cleanName } or null if not prefixed.
 */
function parsePrefixedName(name) {
    const match = name.match(PREFIX_RE);
    if (!match) return null;
    const [, letter, table, cleanName] = match;
    const itemIdOffset = itemIdOf(SECTION_LETTERS[letter], parseInt(table, 10), 0);
    return { itemIdOffset, cleanName };
}

/**
 * Find a transaction-area input whose normalized data-label matches `cleanName`
 * and whose item-id matches `itemId`.
 */
function findTransactionInput(itemId, cleanName, value) {
    const candidates = document.querySelectorAll(
        `[item-id="${itemId}"]`
    );
    for (const c of candidates) {
        const dataLabel = c.getAttribute("data-label");
        if (!dataLabel) continue;
        if (normalizeLabel(dataLabel) !== cleanName) continue;
        if (c.type === "radio" && value !== undefined && c.value !== value) {
            continue;
        }
        return c;
    }
    return null;
}

/**
 * Find input for a claim_field by name. Tries direct data-label match first,
 * then falls back to (item-id, normalized label) lookup for prefixed names
 * (B.x/C.x/D.x), and finally case-insensitive direct match.
 */
function findFieldInput(name, value) {
    // Prefer form controls — the wrapper div for checkbox-groups inherits the
    // same data-label, and querySelector would otherwise return the wrapper.
    let input = document.querySelector(
        `input[data-label="${name}"], select[data-label="${name}"], textarea[data-label="${name}"]`
    );
    if (input) return input;

    const parsed = parsePrefixedName(name);
    if (parsed) {
        // Prefixed fields (e.g. B.1/B.4 totals) live in single-row tables,
        // so item-id is offset + 1.
        const itemId = parsed.itemIdOffset + 1;
        input = findTransactionInput(itemId, parsed.cleanName, value);
        if (input) return input;
    }

    // Case-insensitive fallback
    const lower = name.toLowerCase();
    const all = document.querySelectorAll("[data-label]");
    for (const el of all) {
        if (el.getAttribute("data-label").toLowerCase() === lower) {
            return el;
        }
    }
    return null;
}

/**
 * @param {*} member
 * @param {*} config
 */
export default function loadMember(member, config) {
    const claimFields = member.claim_fields || [];
    const claimTransactions = member.claim_transactions || [];
    const claimFiles = member.claim_files || [];

    for (const field of claimFields) {
        let input = findFieldInput(field.name, field.value);
        if (!input) continue;

        switch (input.type) {
            case "text":
            case "tel":
            case "email":
            case "number":
            case "date":
            case "select-one":
            case "hidden":
                input.value = field.value;
                break;
            case "textarea":
                input.innerText = field.value;
                break;
            case "checkbox":
                input.checked = field.value.toUpperCase() === "YES";
                break;
            case "radio":
                input =
                    document.querySelector(
                        `[data-label="${field.name}"][value="${field.value}"]`
                    ) || findFieldInput(field.name, field.value);
                if (input) input.checked = true;
                break;
        }
    }

    for (const stored of claimTransactions) {
        // Stored item-ids may predate the widened scheme; match the rendered one.
        const transaction = { ...stored, item_id: toCurrentItemId(stored.item_id) };
        const inputSelector = `[data-label="${transaction.name}"][item-id="${transaction.item_id}"]`;
        const parsed = parsePrefixedName(transaction.name);
        const cleanName = parsed ? parsed.cleanName : transaction.name;
        const findFn = () =>
            document.querySelector(inputSelector) ||
            findTransactionInput(transaction.item_id, cleanName);

        // Within a table, the first row uses (offset + 1); higher rows need to
        // be added via the "Add Row" button before the input exists.
        const { section, table, row: rowInTable } = decodeItemId(transaction.item_id);
        const offset = parsed ? parsed.itemIdOffset : itemIdOf(section, table, 0);
        if (rowInTable > 1 && !findFn()) {
            const child =
                document.querySelector(`[data-label="${transaction.name}"]`) ||
                findTransactionInput(offset + 1, cleanName);
            if (child) {
                const parent = child.closest("table");
                const parentID = parent.id.split("_")[1];
                document
                    .getElementById("add_transactions_" + parentID)
                    .click();
            }
        }

        // added requestAnimationFrame to ensure the input is available
        requestAnimationFrame(() => {
            let input = findFn(); // RH - changed to let since const would fail for case `radio`
            if (!input) return;

            switch (input.type) {
                case "text":
                case "tel":
                case "email":
                case "number":
                case "date":
                case "select-one":
                case "hidden":
                    input.value = transaction.value;
                    break;
                case "textarea":
                    input.innerText = transaction.value;
                    break;
                case "checkbox":
                    input.checked = transaction.value.toUpperCase() === "YES";
                    break;
                case "radio":
                    input =
                        document.querySelector(
                            `[data-label="${transaction.name}"][value="${transaction.value}"][item-id="${transaction.item_id}"]`
                        ) ||
                        findTransactionInput(
                            transaction.item_id,
                            cleanName,
                            transaction.value
                        );
                    if (input) input.checked = true;
                    break;
            }
        });
    }

    // Handle claim files for multiple documentation sections
    if (claimFiles.length > 0) {
        // Group files by their documentation section (doc_id)
        const filesBySection = {};

        for (const file of claimFiles) {
            const docId = getDocId(config, file.form_id);
            if (!filesBySection[docId]) {
                filesBySection[docId] = [];
            }
            filesBySection[docId].push(file);
        }

        // Process each documentation section
        for (const [docId, files] of Object.entries(filesBySection)) {
            const list = document.getElementById(`file-list-${docId}`);
            const noFilesElement = document.getElementById(`no-files-${docId}`);

            if (list && files.length > 0) {
                list.classList.remove("d-none");
                if (noFilesElement) {
                    noFilesElement.classList.add("d-none");
                }

                for (const file of files) {
                    const li = document.createElement("li");
                    li.setAttribute("disabled", "disabled");
                    li.innerHTML = `<span class="float-right badge ${file.failed ? "bg-danger" : "bg-success"}">${file.failed ? "FAILED" : "Uploaded"}</span><span class="filename"><strong>${file.name}</strong></span><span class="description">(${file.description})</span>`;
                    list.appendChild(li);
                }
            }
        }

        // Fallback for legacy single file-list support
        const legacyList = document.getElementById("file-list");
        const legacyNoFiles = document.getElementById("no-files");

        if (legacyList && claimFiles.length > 0) {
            legacyList.classList.remove("d-none");
            if (legacyNoFiles) {
                legacyNoFiles.classList.add("d-none");
            }

            for (const file of claimFiles) {
                const li = document.createElement("li");
                li.setAttribute("disabled", "disabled");
                li.innerHTML = `<span class="float-right badge ${file.failed ? "bg-danger" : "bg-success"}">${file.failed ? "FAILED" : "Uploaded"}</span><span class="filename"><strong>${file.name}</strong></span><span class="description">(${file.description})</span>`;
                legacyList.appendChild(li);
            }
        }
    }

    for (const key in member) {
        if (!member.hasOwnProperty(key)) continue;

        const field = document.getElementById(key);
        if (!field) continue;

        field.value = member[key];
    }

    const country = document.getElementById("country");
    if (country.value === "") country.value = "USA";
    if (country.value !== "USA") {
        document.querySelectorAll("#state,#zip_code").forEach((input) => {
            toggleFieldAttribute(input, "required", false);
            input.parentNode.classList.add("d-none");
        });
        document.querySelectorAll("#province,#postal_code").forEach((input) => {
            toggleFieldAttribute(input, "required", true);
            input.parentNode.classList.remove("d-none");
        });
        toggleForeignPhone(country.value);
    }

    // if (member.claim_id) $('#confirm_email_address').val($('#email_address').val());
    if (member.claim_id)
        document.getElementById("confirm_email_address").value =
            document.getElementById("email_address").value;
}

function getDocId(config, label) {
    // return the config key whose data_file_label matches the label
    for (const key in config) {
        if (config[key].data_file_label === label) {
            return key;
        }
    }
    return "documentation";
}
